<script setup lang="ts">
import Typography from '@components/Typography.vue';
import type { SelectionItem } from '@models/selectionItem.ts';
import { storeToRefs } from 'pinia';
import useSelectionItemStore from '@organisms/offer/selection-items/stores/useSelectionItemStore';
import Icon from '@components/Icon.vue';
import { computed, nextTick, onMounted, ref, useTemplateRef } from 'vue';
import type { Proposal } from '@models/proposal.ts';
import { useSelectionItemCommentsStore } from '@organisms/offer/selection-items/stores/useSelectionItemCommentsStore';
import UserAuth from '@tools/user/auth';
import ContentVueEditor from '@components/ContentVueEditor.vue';
import { trans } from '@elements/translation';
import IconFavicon from '@icons/ph_favicon.svg';
import { useLoading } from '@composables/useLoading.ts';
import { SelectionItemProductTour } from '@enums/productTour.ts';
import Spinner from '@components/Spinner.vue';
import Button from '@components/Button.vue';
import PipedriveUserPicture from '@components/PipedriveUserPicture.vue';

const props = defineProps<{ selectionItem: SelectionItem }>();

const selectionItemStore = useSelectionItemStore();
const { accountManager, firstItemId } = storeToRefs(selectionItemStore);
const { updateProposalComment, createProposalComment } = selectionItemStore;

const { isLoading, startLoading, stopLoading } = useLoading();

const proposal = computed<Proposal | null>(() => props.selectionItem.proposal);

const { openCommentsModal } = useSelectionItemCommentsStore();

const isEditMode = ref<boolean>(false);
const commentText = ref<string>(props.selectionItem.proposal?.comment ?? '');
const savedComment = ref<string>(props.selectionItem.proposal?.comment ?? '');

const reviewComment = useTemplateRef<typeof Typography>('reviewComment');
const isCommentCropped = ref<boolean>(false);

const companyName = computed<string>(() => window.COMPANY_NAME);

const isEditable = computed<boolean>(() => UserAuth.isImpersonated() && !props.selectionItem.is_removed);

const shouldTruncateComment = (): void => {
  if (!reviewComment.value) {
    return;
  }

  const comment = reviewComment.value.$el;

  isCommentCropped.value = comment?.clientHeight < comment?.scrollHeight;
};

const saveComment = async (): Promise<void> => {
  if (savedComment.value === commentText.value) {
    isEditMode.value = false;
    return;
  }

  startLoading();

  let success: boolean;

  if (!proposal.value) {
    success = await createProposalComment({
      selectionItemId: props.selectionItem.id,
      comment: commentText.value,
    });
  } else {
    success = await updateProposalComment({
      proposalId: proposal.value.id,
      comment: commentText.value,
    });
  }

  stopLoading();

  if (!success) {
    return;
  }

  savedComment.value = commentText.value;
  isEditMode.value = false;

  await nextTick();
  shouldTruncateComment();
};

const commentsCounterId = computed<SelectionItemProductTour | undefined>(
  () => (props.selectionItem.id === firstItemId.value ? SelectionItemProductTour.TOUR_ADD_COMMENT : undefined),
);

onMounted(shouldTruncateComment);
</script>

<template>
  <div class="selection-item-review">
    <ContentVueEditor
      v-if="isEditMode"
      v-model="commentText"
      class="selection-item-review__editor"
    />

    <template v-else>
      <div
        v-if="proposal?.comment"
        class="selection-item-review__content"
      >
        <div
          v-if="accountManager"
          class="selection-item-review__author"
        >
          <PipedriveUserPicture :user="accountManager" :size="32" />

          <Typography variant="body-small-bold">
            {{ accountManager.firstname }} ({{ companyName }})
          </Typography>
        </div>

        <!-- eslint-disable vue/no-v-html vue/no-v-text-v-html-on-component -->
        <Typography
          ref="reviewComment"
          variant="body-small"
          class="selection-item-review__comment"
          font-inherit
          v-html="proposal.comment"
        />
        <!-- eslint-enable vue/no-v-html vue/no-v-text-v-html-on-component -->

        <Button
          v-show="isCommentCropped"
          variant="invisible"
          class="selection-item-review__expand-button"
          @click="openCommentsModal(selectionItem)"
        >
          <Typography
            variant="body-small"
            as="span"
            color="gray-darker"
          >
            {{ trans('global.see_more') }}
          </Typography>
        </Button>
      </div>

      <div
        v-else
        class="selection-item-review__empty-state"
      >
        <div class="selection-item-review__am-picture">
          <PipedriveUserPicture
            v-if="accountManager"
            :user="accountManager"
            :size="40"
          />

          <span class="selection-item-review__logo">
            <Icon
              :svg="IconFavicon"
              width="24"
              height="24"
            />
          </span>
        </div>

        <div>
          <Typography
            variant="body-small-bold"
            color="gray-darker"
          >
            {{ trans('selection_item.review.question') }}
          </Typography>

          <Typography
            variant="body-small"
            color="gray-darker"
          >
            {{ trans('selection_item.review.advice') }}

            <a
              href="#"
              @click="openCommentsModal(selectionItem)"
            >
              <Typography
                variant="body-small"
                color="gray-darker"
              >
                {{ trans('selection_item.review.advice.link') }}
              </Typography>
            </a>

            <Button
              v-if="isEditable"
              variant="white"
              class="btn-link selection-item-review__btn-link selection-item-review__am-button"
              @click="isEditMode = true"
            >
              {{ trans('selection_item.review.add') }}
            </Button>
          </Typography>
        </div>
      </div>
    </template>

    <div
      v-if="isEditable"
      class="selection-item-review__actions"
    >
      <Spinner v-if="isLoading" />

      <Button
        v-else-if="isEditMode"
        class="btn-link selection-item-review__btn-link"
        @click="saveComment"
      >
        {{ trans('selection_item.comment.button.save') }}
      </Button>

      <Button
        v-else-if="proposal?.comment"
        class="btn-link selection-item-review__btn-link"
        @click="isEditMode = true"
      >
        {{ trans('selection_item.comment.button.edit') }}
      </Button>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '@styles/variables' as *;
@use '@styles/mixins' as *;

.selection-item-review {
  border-radius: rem(8);
  border: 1px solid $colorGray;
  padding: rem(12);
  display: flex;
  flex-direction: column;
  justify-content: center;

  &__editor {
    flex: 1;
  }

  &__content {
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: rem(8);
  }

  &__comment {
    flex: 1;
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 6;
  }

  &__author {
    display: flex;
    align-items: center;
    gap: rem(8);
  }

  &__empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: rem(8);
    text-align: center;
  }

  &__am-picture {
    position: relative;
    width: rem(40 + 8);
    height: rem(40 + 8);
    display: flex;
  }

  &__logo {
    position: absolute;
    bottom: rem(-12);
    right: 0;
  }

  &__am-button {
    margin-top: rem(10);
  }

  &__btn-link {
    flex: 0;
    height: fit-content;
    width: fit-content;
    padding: 0 rem(4);

    &:hover {
      background-color: $colorGrayLighter;
      color: $colorBlue;
    }
  }

  &__actions {
    margin-top: rem(2);
    display: flex;
  }

  &__expand-button {
    height: fit-content;
    width: fit-content;
    cursor: pointer;
    padding: 0 rem(4);
    text-decoration: underline;
  }
}
</style>
